/**
 * Parwaaz backend — the memory-adapter Platform.
 *
 * Works identically in Node (dev server, tests) and the browser (in-browser demo): all crypto
 * is `@noble/hashes` (synchronous, no WebCrypto await needed) and randomness comes from
 * `globalThis.crypto.getRandomValues`, present in Node 20+ and every modern browser. There is
 * one lock, an integer depth counter — the memory store never really contends because every
 * call is synchronous, so this exists only to make `withLock` calls safely re-entrant.
 */
import { hmac } from '@noble/hashes/hmac';
import { sha256 } from '@noble/hashes/sha2';
import { bytesToHex, utf8ToBytes } from '@noble/hashes/utils';
import type { Platform } from '../../store';

const B64URL_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

function bytesToBase64Url(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const b0 = bytes[i]!;
    const b1 = bytes[i + 1];
    const b2 = bytes[i + 2];
    const triplet = (b0 << 16) | ((b1 ?? 0) << 8) | (b2 ?? 0);
    out += B64URL_ALPHABET[(triplet >> 18) & 63];
    out += B64URL_ALPHABET[(triplet >> 12) & 63];
    out += b1 !== undefined ? B64URL_ALPHABET[(triplet >> 6) & 63] : '';
    out += b2 !== undefined ? B64URL_ALPHABET[triplet & 63] : '';
  }
  return out;
}

export interface MailMessage {
  to: string;
  subject: string;
  html: string;
  text?: string;
}

/** The memory Platform, plus the extras tests and notify.ts use (never referenced through `Platform`). */
export type MemoryPlatform = Platform & {
  outbox: MailMessage[];
  fetch?: typeof fetch;
};

export interface CreatePlatformOpts {
  /** Called in addition to the default console-log + outbox push, e.g. to also print in a demo UI. */
  sendMail?: (to: string, subject: string, html: string, text?: string) => void;
  /** Override the fetch implementation (tests can stub it); defaults to the global fetch if present. */
  fetch?: typeof fetch;
}

export function createPlatform(runtime: Platform['runtime'], opts: CreatePlatformOpts = {}): MemoryPlatform {
  const outbox: MailMessage[] = [];
  let lockDepth = 0;

  const platform: MemoryPlatform = {
    runtime,

    sha256: (input) => bytesToHex(sha256(utf8ToBytes(input))),
    sha256B64url: (input) => bytesToBase64Url(sha256(utf8ToBytes(input))),
    hmacSha256B64url: (message, key) => bytesToBase64Url(hmac(sha256, utf8ToBytes(key), utf8ToBytes(message))),

    uuid: () => globalThis.crypto.randomUUID(),
    now: () => new Date().toISOString(),
    randomToken: (bytes) => {
      const buf = new Uint8Array(bytes);
      globalThis.crypto.getRandomValues(buf);
      return bytesToBase64Url(buf);
    },

    withLock: (fn) => {
      lockDepth += 1;
      try {
        return fn();
      } finally {
        lockDepth -= 1;
      }
    },

    sendMail: (to, subject, html, text) => {
      outbox.push({ to, subject, html, text });
      console.log(`[parwaaz mail] → ${to}: ${subject}`);
      opts.sendMail?.(to, subject, html, text);
    },

    log: (...args) => console.log('[parwaaz]', ...args),

    outbox,
    fetch: opts.fetch ?? (typeof fetch !== 'undefined' ? fetch.bind(globalThis) : undefined),
  };

  return platform;
}

/** Named per the plan's runtime list — identical to createPlatform, defaulting to 'devserver'. */
export function createNodePlatform(runtime: Platform['runtime'] = 'devserver', opts: CreatePlatformOpts = {}): MemoryPlatform {
  return createPlatform(runtime, opts);
}

/** Identical implementation; named separately because the in-browser demo boots through here. */
export function createBrowserPlatform(opts: CreatePlatformOpts = {}): MemoryPlatform {
  return createPlatform('demo', opts);
}

export { bytesToBase64Url };
